// src/zod/schemas.ts
import { z } from "zod";
var imageSchema = z.object({
  text: z.string().optional(),
  url: z.string().optional(),
  uri: z.string().optional(),
  accessibilityDescription: z.string().optional()
});
var httpMethodSchema = z.union([
  z.literal("GET"),
  z.literal("POST"),
  z.literal("PUT"),
  z.literal("PATCH"),
  z.literal("DELETE")
]);
var iconNamedSchema = z.object({
  name: z.string()
});
var contextSchema = z.union([
  z.literal("positive"),
  z.literal("neutral"),
  z.literal("warning"),
  z.literal("negative"),
  z.literal("success"),
  z.literal("failure"),
  z.literal("info"),
  z.literal("primary")
]);
var iconTextSchema = z.object({
  text: z.string()
});
var sizeSchema = z.union([
  z.literal("xs"),
  z.literal("sm"),
  z.literal("md"),
  z.literal("lg"),
  z.literal("xl")
]);
var autocapitalizationTypeSchema = z.union([
  z.literal("none"),
  z.literal("characters"),
  z.literal("sentences"),
  z.literal("words")
]);
var alignSchema = z.union([z.literal("left"), z.literal("center"), z.literal("right")]);
var iconSchema = z.union([iconNamedSchema, iconTextSchema]);
var autocompleteTokenSchema = z.union([
  z.literal("on"),
  z.literal("name"),
  z.literal("name-prefix"),
  z.literal("given-name"),
  z.literal("additional-name"),
  z.literal("family-name"),
  z.literal("name-suffix"),
  z.literal("nickname"),
  z.literal("email"),
  z.literal("username"),
  z.literal("new-username"),
  z.literal("new-password"),
  z.literal("password"),
  z.literal("one-time-code"),
  z.literal("job-title"),
  z.literal("organization-name"),
  z.literal("full-street-address"),
  z.literal("street-address-line-1"),
  z.literal("street-address-line-2"),
  z.literal("street-address-line-3"),
  z.literal("address-level-1"),
  z.literal("address-level-2"),
  z.literal("address-level-3"),
  z.literal("address-level-4"),
  z.literal("country-code"),
  z.literal("country-name"),
  z.literal("postal-code"),
  z.literal("credit-card-name"),
  z.literal("credit-card-given-name"),
  z.literal("credit-card-middle-name"),
  z.literal("credit-card-family-name"),
  z.literal("credit-card-number"),
  z.literal("credit-card-expiration"),
  z.literal("credit-card-expiration-month"),
  z.literal("credit-card-expiration-year"),
  z.literal("credit-card-security-code"),
  z.literal("credit-card-type"),
  z.literal("transaction-currency"),
  z.literal("transaction-amount"),
  z.literal("language"),
  z.literal("birthdate"),
  z.literal("birthdate-day"),
  z.literal("birthdate-month"),
  z.literal("birthdate-year"),
  z.literal("gender"),
  z.literal("phone-number"),
  z.literal("phone-country-code"),
  z.literal("phone-national"),
  z.literal("phone-area-code"),
  z.literal("phone-local"),
  z.literal("phone-local-prefix"),
  z.literal("phone-local-suffix"),
  z.literal("phone-extension"),
  z.literal("url"),
  z.literal("photo"),
  z.literal("impp"),
  z.literal("shipping"),
  z.literal("billing"),
  z.literal("home"),
  z.literal("work"),
  z.literal("mobile"),
  z.literal("fax"),
  z.literal("pager")
]);
var loadingIndicatorLayoutSchema = z.object({
  type: z.literal("loading-indicator"),
  size: sizeSchema.optional(),
  control: z.string().optional(),
  margin: sizeSchema.optional()
});
var paragraphLayoutSchema = z.object({
  type: z.literal("paragraph"),
  text: z.string(),
  align: alignSchema.optional(),
  control: z.string().optional(),
  margin: sizeSchema.optional()
});
var dividerLayoutSchema = z.object({
  type: z.literal("divider"),
  control: z.string().optional(),
  margin: sizeSchema.optional()
});
var listLayoutStatusSchema = z.union([
  z.literal("warning"),
  z.literal("neutral"),
  z.literal("positive")
]);
var formLayoutSchemaReferenceSchema = z.object({
  $ref: z.string()
});
var imageLayoutSchema = z.object({
  type: z.literal("image"),
  text: z.string().optional(),
  url: z.string().optional(),
  accessibilityDescription: z.string().optional(),
  content: imageSchema.optional(),
  size: sizeSchema.optional(),
  control: z.string().optional(),
  margin: sizeSchema.optional(),
  align: alignSchema.optional()
});
var statusListLayoutStatusSchema = z.union([
  z.literal("not-done"),
  z.literal("pending"),
  z.literal("done")
]);
var instructionsLayoutItemSchema = z.object({
  text: z.string(),
  context: contextSchema,
  tag: z.string().optional()
});
var modalLayoutTriggerSchema = z.object({
  title: z.string()
});
var searchLayoutSchema = z.object({
  type: z.literal("search"),
  title: z.string(),
  method: httpMethodSchema,
  url: z.string(),
  param: z.string(),
  emptyMessage: z.string().optional(),
  control: z.string().optional(),
  margin: sizeSchema.optional()
});
var infoLayoutSchema = z.object({
  type: z.literal("info"),
  markdown: z.string(),
  align: alignSchema.optional(),
  control: z.string().optional(),
  margin: sizeSchema.optional()
});
var formLayoutSchema = z.object({
  type: z.literal("form"),
  schema: formLayoutSchemaReferenceSchema.optional(),
  schemaId: z.string(),
  control: z.string().optional(),
  margin: sizeSchema.optional()
});
var headingLayoutSchema = z.object({
  type: z.literal("heading"),
  text: z.string(),
  size: sizeSchema.optional(),
  align: alignSchema.optional(),
  control: z.string().optional(),
  margin: sizeSchema.optional()
});
var markdownLayoutSchema = z.object({
  type: z.literal("markdown"),
  content: z.string(),
  align: alignSchema.optional(),
  control: z.string().optional(),
  margin: sizeSchema.optional()
});
var columnsLayoutBiasSchema = z.union([
  z.literal("none"),
  z.literal("left"),
  z.literal("right")
]);
var helpSchema = z.object({
  markdown: z.string()
});
var searchSearchRequestSchema = z.object({
  url: z.string(),
  method: httpMethodSchema,
  param: z.string(),
  query: z.string()
});
var jsonElementSchema = z.lazy(
  () => z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.record(jsonElementSchema),
    z.array(jsonElementSchema)
  ]).nullable()
);
var externalSchema = z.object({
  url: z.string()
});
var stepErrorSchema = z.object({
  error: z.string().optional(),
  validation: jsonElementSchema.optional()
});
var stringSchemaFormatSchema = z.union([
  z.literal("date"),
  z.literal("email"),
  z.literal("numeric"),
  z.literal("password"),
  z.literal("phone-number"),
  z.literal("base64url")
]);
var summarySummariserSchema = z.object({
  defaultTitle: z.string().optional(),
  defaultDescription: z.string().optional(),
  defaultIcon: iconSchema.optional(),
  defaultImage: imageSchema.optional(),
  providesTitle: z.boolean().optional(),
  providesDescription: z.boolean().optional(),
  providesIcon: z.boolean().optional(),
  providesImage: z.boolean().optional()
});
var validateAsyncSchema = z.object({
  param: z.string(),
  method: httpMethodSchema,
  url: z.string()
});
var summaryProviderSchema = z.object({
  providesTitle: z.boolean().optional(),
  providesDescription: z.boolean().optional(),
  providesIcon: z.boolean().optional(),
  providesImage: z.boolean().optional()
});
var uploadSourceSchema = z.union([z.literal("camera"), z.literal("file")]);
var suggestionsValueSchema = z.object({
  label: z.string(),
  value: jsonElementSchema.optional(),
  icon: iconSchema.optional(),
  image: imageSchema.optional(),
  tag: z.string().optional()
});
var navigationStackBehaviorSchema = z.union([
  z.literal("default"),
  z.literal("remove-previous"),
  z.literal("remove-all"),
  z.literal("replace-current")
]);
var actionTypeSchema = z.union([
  z.literal("primary"),
  z.literal("secondary"),
  z.literal("link"),
  z.literal("positive"),
  z.literal("negative")
]);
var summarySchema = z.union([summaryProviderSchema, summarySummariserSchema]);
var linkBehaviorSchema = z.object({
  type: z.literal("link"),
  url: z.string()
});
var linkSchema = z.object({
  url: z.string()
});
var actionSchema = z.object({
  title: z.string().optional(),
  type: actionTypeSchema.optional(),
  disabled: z.boolean().optional(),
  $id: z.string().optional(),
  $ref: z.string().optional(),
  id: z.string().optional(),
  url: z.string().optional(),
  method: httpMethodSchema.optional(),
  exit: z.boolean().optional(),
  result: jsonElementSchema.optional(),
  data: jsonElementSchema.optional(),
  timeout: z.number().optional(),
  skipValidation: z.boolean().optional()
});
var listLayoutItemSchema = z.object({
  description: z.string().optional(),
  status: listLayoutStatusSchema.optional(),
  icon: iconSchema.optional(),
  image: imageSchema.optional(),
  title: z.string().optional(),
  subtitle: z.string().optional(),
  value: z.string().optional(),
  subvalue: z.string().optional(),
  tag: z.string().optional()
});
var instructionsLayoutSchema = z.object({
  type: z.literal("instructions"),
  title: z.string().optional(),
  items: z.array(instructionsLayoutItemSchema),
  control: z.string().optional(),
  margin: sizeSchema.optional()
});
var reviewLayoutFieldSchema = z.object({
  label: z.string(),
  value: z.string(),
  rawValue: z.string().optional(),
  help: helpSchema.optional(),
  tag: z.string().optional()
});
var searchResultSearchSchema = z.object({
  type: z.literal("search"),
  title: z.string(),
  description: z.string().optional(),
  icon: iconSchema.optional(),
  image: imageSchema.optional(),
  value: searchSearchRequestSchema
});
var searchResultActionSchema = z.object({
  type: z.literal("action"),
  title: z.string(),
  description: z.string().optional(),
  icon: iconSchema.optional(),
  image: imageSchema.optional(),
  value: actionSchema
});
var actionResponseBodySchema = z.object({
  action: actionSchema
});
var errorResponseBodySchema = z.object({
  refreshFormUrl: z.string().optional(),
  analytics: z.record(z.string()).optional(),
  error: z.string().optional(),
  validation: jsonElementSchema.optional(),
  refreshUrl: z.string().optional()
});
var suggestionsSchema = z.object({
  values: z.array(suggestionsValueSchema)
});
var navigationBackBehaviorSchema = z.object({
  title: z.string().optional(),
  action: actionSchema
});
var actionBehaviorSchema = z.object({
  type: z.literal("action"),
  action: actionSchema
});
var containerBehaviorSchema = z.object({
  action: actionSchema.optional(),
  link: linkSchema.optional()
});
var behaviorSchema = z.union([
  actionBehaviorSchema,
  containerBehaviorSchema,
  linkBehaviorSchema
]);
var alertLayoutCallToActionSchema = z.object({
  title: z.string(),
  accessibilityDescription: z.string().optional(),
  behavior: behaviorSchema
});
var listLayoutCallToActionSchema = z.object({
  title: z.string(),
  accessibilityDescription: z.string().optional(),
  behavior: behaviorSchema
});
var decisionLayoutOptionSchema = z.object({
  action: actionSchema.optional(),
  title: z.string(),
  description: z.string().optional(),
  disabled: z.boolean().optional(),
  icon: iconSchema.optional(),
  image: imageSchema.optional(),
  behavior: behaviorSchema.optional(),
  tag: z.string().optional()
});
var itemCallToActionSchema = z.object({
  title: z.string(),
  accessibilityDescription: z.string().optional(),
  behavior: behaviorSchema
});
var buttonLayoutSchema = z.object({
  type: z.literal("button"),
  action: actionSchema.optional(),
  size: sizeSchema.optional(),
  title: z.string().optional(),
  behavior: behaviorSchema.optional(),
  context: contextSchema.optional(),
  disabled: z.boolean().optional(),
  pinOrder: z.number().optional(),
  control: z.string().optional(),
  margin: sizeSchema.optional()
});
var searchResultSchema = z.union([searchResultActionSchema, searchResultSearchSchema]);
var linkHandlerSchema = z.object({
  regexPattern: z.string(),
  behavior: behaviorSchema.optional()
});
var navigationSchema = z.object({
  backButton: navigationBackBehaviorSchema.optional(),
  back: navigationBackBehaviorSchema.optional(),
  stackBehavior: navigationStackBehaviorSchema.optional()
});
var pollingOnErrorSchema = z.object({
  action: actionSchema.optional(),
  behavior: behaviorSchema.optional()
});
var reviewLayoutCallToActionSchema = z.object({
  action: actionSchema.optional(),
  title: z.string(),
  accessibilityDescription: z.string().optional(),
  behavior: behaviorSchema.optional()
});
var alertLayoutSchema = z.object({
  type: z.literal("alert"),
  markdown: z.string(),
  context: contextSchema.optional(),
  control: z.string().optional(),
  margin: sizeSchema.optional(),
  callToAction: alertLayoutCallToActionSchema.optional()
});
var listLayoutSchema = z.object({
  type: z.literal("list"),
  title: z.string().optional(),
  callToAction: listLayoutCallToActionSchema.optional(),
  items: z.array(listLayoutItemSchema),
  control: z.string().optional(),
  margin: sizeSchema.optional()
});
var decisionLayoutSchema = z.object({
  type: z.literal("decision"),
  title: z.string().optional(),
  options: z.array(decisionLayoutOptionSchema),
  control: z.string().optional(),
  margin: sizeSchema.optional()
});
var statusListLayoutItemSchema = z.object({
  title: z.string(),
  description: z.string().optional(),
  icon: iconSchema,
  status: statusListLayoutStatusSchema.optional(),
  callToAction: itemCallToActionSchema.optional(),
  tag: z.string().optional()
});
var reviewLayoutSchema = z.object({
  type: z.literal("review"),
  orientation: z.string().optional(),
  action: actionSchema.optional(),
  fields: z.array(reviewLayoutFieldSchema),
  title: z.string().optional(),
  callToAction: reviewLayoutCallToActionSchema.optional(),
  control: z.string().optional(),
  margin: sizeSchema.optional()
});
var searchResponseBodySchema = z.object({
  results: z.array(searchResultSchema)
});
var pollingSchema = z.object({
  interval: z.number().optional(),
  url: z.string(),
  delay: z.number().optional(),
  timeout: z.number().optional(),
  maxAttempts: z.number(),
  onError: pollingOnErrorSchema
});
var blobSchemaSchema = z.object({
  type: z.literal("blob"),
  promoted: z.boolean().optional(),
  $id: z.string().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  control: z.string().optional(),
  hidden: z.boolean().optional(),
  icon: iconSchema.optional(),
  image: imageSchema.optional(),
  keywords: z.array(z.string()).optional(),
  summary: summaryProviderSchema.optional(),
  analyticsId: z.string().optional(),
  validationAsync: validateAsyncSchema.optional(),
  validationMessages: z.record(z.string()).optional(),
  alert: alertLayoutSchema.optional(),
  cameraConfig: jsonElementSchema.optional(),
  accepts: z.array(z.string()).optional(),
  maxSize: z.number().optional(),
  source: uploadSourceSchema.optional(),
  disabled: z.boolean().optional()
});
var constSchemaSchema = z.object({
  hidden: z.boolean().optional(),
  alert: alertLayoutSchema.optional(),
  control: z.string().optional(),
  promoted: z.boolean().optional(),
  $id: z.string().optional(),
  const: jsonElementSchema,
  title: z.string().optional(),
  description: z.string().optional(),
  icon: iconSchema.optional(),
  image: imageSchema.optional(),
  keywords: z.array(z.string()).optional(),
  summary: summaryProviderSchema.optional(),
  analyticsId: z.string().optional(),
  disabled: z.boolean().optional()
});
var statusListLayoutSchema = z.object({
  type: z.literal("status-list"),
  items: z.array(statusListLayoutItemSchema),
  title: z.string().optional(),
  control: z.string().optional(),
  margin: sizeSchema.optional()
});
var layoutSchema = z.lazy(
  () => z.union([
    alertLayoutSchema,
    boxLayoutSchema,
    buttonLayoutSchema,
    columnsLayoutSchema,
    decisionLayoutSchema,
    dividerLayoutSchema,
    formLayoutSchema,
    headingLayoutSchema,
    imageLayoutSchema,
    infoLayoutSchema,
    instructionsLayoutSchema,
    listLayoutSchema,
    loadingIndicatorLayoutSchema,
    markdownLayoutSchema,
    modalLayoutSchema,
    paragraphLayoutSchema,
    reviewLayoutSchema,
    searchLayoutSchema,
    statusListLayoutSchema
  ])
);
var boxLayoutSchema = z.lazy(
  () => z.object({
    type: z.literal("box"),
    components: z.array(layoutSchema),
    width: sizeSchema.optional(),
    border: z.boolean().optional(),
    control: z.string().optional(),
    margin: sizeSchema.optional()
  })
);
var columnsLayoutSchema = z.lazy(
  () => z.object({
    type: z.literal("columns"),
    left: z.array(layoutSchema),
    right: z.array(layoutSchema),
    bias: columnsLayoutBiasSchema.optional(),
    control: z.string().optional(),
    margin: sizeSchema.optional()
  })
);
var modalLayoutSchema = z.lazy(
  () => z.object({
    type: z.literal("modal"),
    control: z.string().optional(),
    margin: sizeSchema.optional(),
    trigger: modalLayoutTriggerSchema,
    content: modalLayoutContentSchema
  })
);
var modalLayoutContentSchema = z.lazy(
  () => z.object({
    title: z.string().optional(),
    components: z.array(layoutSchema)
  })
);
var stepSchema = z.lazy(
  () => z.object({
    key: z.string().optional(),
    type: z.string().optional(),
    actions: z.array(actionSchema).optional(),
    refreshFormUrl: z.string().optional(),
    id: z.string(),
    title: z.string(),
    schemas: z.array(schemaSchema),
    layout: z.array(layoutSchema),
    description: z.string().optional(),
    model: jsonElementSchema.optional(),
    external: externalSchema.optional(),
    polling: pollingSchema.optional(),
    linkHandlers: z.array(linkHandlerSchema).optional(),
    analytics: z.record(z.string()).optional(),
    errors: stepErrorSchema.optional(),
    navigation: navigationSchema.optional(),
    refreshUrl: z.string().optional(),
    control: z.string().optional(),
    refreshAfter: z.string().optional()
  })
);
var schemaSchema = z.lazy(
  () => z.union([
    allOfSchemaSchema,
    arraySchemaSchema,
    blobSchemaSchema,
    booleanSchemaSchema,
    constSchemaSchema,
    integerSchemaSchema,
    numberSchemaSchema,
    objectSchemaSchema,
    oneOfSchemaSchema,
    stringSchemaSchema
  ])
);
var allOfSchemaSchema = z.lazy(
  () => z.object({
    disabled: z.boolean().optional(),
    promoted: z.boolean().optional(),
    allOf: z.array(schemaSchema),
    $id: z.string().optional(),
    title: z.string().optional(),
    description: z.string().optional(),
    control: z.string().optional(),
    hidden: z.boolean().optional(),
    icon: iconSchema.optional(),
    image: imageSchema.optional(),
    keywords: z.array(z.string()).optional(),
    summary: summaryProviderSchema.optional(),
    analyticsId: z.string().optional(),
    alert: alertLayoutSchema.optional()
  })
);
var arraySchemaSchema = z.lazy(
  () => z.union([arraySchemaListSchema, arraySchemaTupleSchema])
);
var booleanSchemaSchema = z.lazy(
  () => z.object({
    type: z.literal("boolean"),
    autofillProvider: z.string().optional(),
    promoted: z.boolean().optional(),
    refreshFormOnChange: z.boolean().optional(),
    refreshUrl: z.string().optional(),
    refreshFormUrl: z.string().optional(),
    $id: z.string().optional(),
    title: z.string().optional(),
    description: z.string().optional(),
    control: z.string().optional(),
    default: z.boolean().optional(),
    hidden: z.boolean().optional(),
    disabled: z.boolean().optional(),
    icon: iconSchema.optional(),
    image: imageSchema.optional(),
    keywords: z.array(z.string()).optional(),
    summary: summaryProviderSchema.optional(),
    analyticsId: z.string().optional(),
    persistAsync: persistAsyncSchema.optional(),
    refreshStepOnChange: z.boolean().optional(),
    validationAsync: validateAsyncSchema.optional(),
    alert: alertLayoutSchema.optional(),
    autofillKey: z.string().optional(),
    help: helpSchema.optional()
  })
);
var integerSchemaSchema = z.lazy(
  () => z.object({
    type: z.literal("integer"),
    autofillProvider: z.string().optional(),
    promoted: z.boolean().optional(),
    refreshFormOnChange: z.boolean().optional(),
    refreshUrl: z.string().optional(),
    refreshFormUrl: z.string().optional(),
    placeholder: z.string().optional(),
    minimum: z.number().optional(),
    maximum: z.number().optional(),
    $id: z.string().optional(),
    title: z.string().optional(),
    description: z.string().optional(),
    control: z.string().optional(),
    default: z.number().optional(),
    hidden: z.boolean().optional(),
    disabled: z.boolean().optional(),
    icon: iconSchema.optional(),
    image: imageSchema.optional(),
    keywords: z.array(z.string()).optional(),
    summary: summaryProviderSchema.optional(),
    analyticsId: z.string().optional(),
    persistAsync: persistAsyncSchema.optional(),
    refreshStepOnChange: z.boolean().optional(),
    validationAsync: validateAsyncSchema.optional(),
    validationMessages: z.record(z.string()).optional(),
    alert: alertLayoutSchema.optional(),
    autocompleteHint: z.array(autocompleteTokenSchema).optional(),
    autofillKey: z.string().optional(),
    help: helpSchema.optional()
  })
);
var numberSchemaSchema = z.lazy(
  () => z.object({
    type: z.literal("number"),
    autofillProvider: z.string().optional(),
    promoted: z.boolean().optional(),
    refreshFormOnChange: z.boolean().optional(),
    refreshUrl: z.string().optional(),
    refreshFormUrl: z.string().optional(),
    placeholder: z.string().optional(),
    minimum: z.number().optional(),
    maximum: z.number().optional(),
    $id: z.string().optional(),
    title: z.string().optional(),
    description: z.string().optional(),
    control: z.string().optional(),
    default: z.number().optional(),
    hidden: z.boolean().optional(),
    disabled: z.boolean().optional(),
    icon: iconSchema.optional(),
    image: imageSchema.optional(),
    keywords: z.array(z.string()).optional(),
    summary: summaryProviderSchema.optional(),
    analyticsId: z.string().optional(),
    persistAsync: persistAsyncSchema.optional(),
    refreshStepOnChange: z.boolean().optional(),
    validationAsync: validateAsyncSchema.optional(),
    validationMessages: z.record(z.string()).optional(),
    alert: alertLayoutSchema.optional(),
    autocompleteHint: z.array(autocompleteTokenSchema).optional(),
    autofillKey: z.string().optional(),
    help: helpSchema.optional()
  })
);
var objectSchemaSchema = z.lazy(
  () => z.object({
    type: z.literal("object"),
    disabled: z.boolean().optional(),
    promoted: z.boolean().optional(),
    help: helpSchema.optional(),
    properties: z.record(schemaSchema),
    displayOrder: z.array(z.string()),
    required: z.array(z.string()).optional(),
    $id: z.string().optional(),
    title: z.string().optional(),
    description: z.string().optional(),
    control: z.string().optional(),
    hidden: z.boolean().optional(),
    icon: iconSchema.optional(),
    image: imageSchema.optional(),
    keywords: z.array(z.string()).optional(),
    summary: summaryProviderSchema.optional(),
    analyticsId: z.string().optional(),
    alert: alertLayoutSchema.optional()
  })
);
var oneOfSchemaSchema = z.lazy(
  () => z.object({
    autofillProvider: z.string().optional(),
    promoted: z.boolean().optional(),
    refreshFormOnChange: z.boolean().optional(),
    refreshUrl: z.string().optional(),
    refreshFormUrl: z.string().optional(),
    promotion: jsonElementSchema.optional(),
    oneOf: z.array(schemaSchema),
    placeholder: z.string().optional(),
    $id: z.string().optional(),
    title: z.string().optional(),
    description: z.string().optional(),
    control: z.string().optional(),
    default: jsonElementSchema.optional(),
    hidden: z.boolean().optional(),
    icon: iconSchema.optional(),
    image: imageSchema.optional(),
    keywords: z.array(z.string()).optional(),
    summary: summaryProviderSchema.optional(),
    analyticsId: z.string().optional(),
    refreshStepOnChange: z.boolean().optional(),
    alert: alertLayoutSchema.optional(),
    help: helpSchema.optional(),
    autocompleteHint: z.array(autocompleteTokenSchema).optional(),
    autofillKey: z.string().optional(),
    validationMessages: z.record(z.string()).optional(),
    disabled: z.boolean().optional()
  })
);
var stringSchemaSchema = z.lazy(
  () => z.object({
    type: z.literal("string"),
    autofillProvider: z.string().optional(),
    promoted: z.boolean().optional(),
    refreshFormOnChange: z.boolean().optional(),
    refreshUrl: z.string().optional(),
    refreshFormUrl: z.string().optional(),
    format: stringSchemaFormatSchema.optional(),
    displayFormat: z.string().optional(),
    placeholder: z.string().optional(),
    minLength: z.number().optional(),
    maxLength: z.number().optional(),
    minimum: z.string().optional(),
    maximum: z.string().optional(),
    pattern: z.string().optional(),
    autocapitalization: autocapitalizationTypeSchema.optional(),
    autocorrect: z.boolean().optional(),
    $id: z.string().optional(),
    title: z.string().optional(),
    description: z.string().optional(),
    control: z.string().optional(),
    default: z.string().optional(),
    hidden: z.boolean().optional(),
    disabled: z.boolean().optional(),
    icon: iconSchema.optional(),
    image: imageSchema.optional(),
    keywords: z.array(z.string()).optional(),
    summary: summaryProviderSchema.optional(),
    analyticsId: z.string().optional(),
    persistAsync: persistAsyncSchema.optional(),
    refreshStepOnChange: z.boolean().optional(),
    validationAsync: validateAsyncSchema.optional(),
    validationMessages: z.record(z.string()).optional(),
    alert: alertLayoutSchema.optional(),
    cameraConfig: jsonElementSchema.optional(),
    accepts: z.array(z.string()).optional(),
    maxSize: z.number().optional(),
    source: uploadSourceSchema.optional(),
    autocompleteHint: z.array(autocompleteTokenSchema).optional(),
    autofillKey: z.string().optional(),
    help: helpSchema.optional(),
    suggestions: suggestionsSchema.optional()
  })
);
var arraySchemaListSchema = z.lazy(
  () => z.object({
    type: z.literal("array"),
    promoted: z.boolean().optional(),
    $id: z.string().optional(),
    items: schemaSchema,
    addItemTitle: z.string(),
    editItemTitle: z.string(),
    minItems: z.number().optional(),
    maxItems: z.number().optional(),
    placeholder: z.string().optional(),
    title: z.string().optional(),
    description: z.string().optional(),
    control: z.string().optional(),
    hidden: z.boolean().optional(),
    icon: iconSchema.optional(),
    image: imageSchema.optional(),
    keywords: z.array(z.string()).optional(),
    summary: summarySummariserSchema.optional(),
    analyticsId: z.string().optional(),
    persistAsync: persistAsyncSchema.optional(),
    validationAsync: validateAsyncSchema.optional(),
    alert: alertLayoutSchema.optional(),
    validationMessages: z.record(z.string()).optional(),
    disabled: z.boolean().optional()
  })
);
var persistAsyncSchema = z.lazy(
  () => z.object({
    param: z.string(),
    idProperty: z.string(),
    schema: schemaSchema,
    url: z.string(),
    method: httpMethodSchema
  })
);
var arraySchemaTupleSchema = z.lazy(
  () => z.object({
    type: z.literal("array"),
    promoted: z.boolean().optional(),
    $id: z.string().optional(),
    items: z.array(schemaSchema),
    title: z.string().optional(),
    description: z.string().optional(),
    control: z.string().optional(),
    hidden: z.boolean().optional(),
    icon: iconSchema.optional(),
    image: imageSchema.optional(),
    keywords: z.array(z.string()).optional(),
    summary: summaryProviderSchema.optional(),
    analyticsId: z.string().optional(),
    persistAsync: persistAsyncSchema.optional(),
    validationAsync: validateAsyncSchema.optional(),
    alert: alertLayoutSchema.optional()
  })
);

// src/zod/validators.ts
var validateStep = (step) => validate(step, stepSchema);
var validateActionResponse = (response) => validate(response, actionResponseBodySchema);
var validateErrorResponse = (response) => validate(response, errorResponseBodySchema);
var validate = (value, schema) => {
  const parseResult = schema.safeParse(value);
  if (parseResult.success) {
    return { valid: true };
  }
  return {
    valid: false,
    errors: JSON.stringify(flattenZodErrors(parseResult.error.errors))
  };
};
var flattenZodErrors = (zodError) => zodError.flatMap((e) => {
  if (e.code === "invalid_type") {
    return e;
  }
  if (e.code === "invalid_union") {
    return e.unionErrors.flatMap(
      (ue) => ue.issues.every((issue) => issue.code !== "invalid_literal") ? flattenZodErrors(ue.issues) : void 0
    );
  }
  return void 0;
}).filter(Boolean);
export {
  validateActionResponse,
  validateErrorResponse,
  validateStep
};
